import React, { useState, useMemo, useEffect, useRef } from 'react';
import SortBySelect from '../../dashboard-shared/sort-by-select/sort-by-select';
import LineGraph from './line-graph';
import Modal from 'react-bootstrap/Modal';
import DateRangePicker from '../../../../components/date-range-picker/date-range-picker';
import dayjs from '../../../../util/dayjs-config';
import '../active-tasks-bar-graph/active-strs-bar-chart.scss';
import '../../dashboard-shared/completed-vs-overdue.scss';
import FluentUiIcon from '../../../../assets/fluent-ui-icon.jsx';
import { FluentButton } from "../../../../components/FluentUI/Fluent-Button";
import { formatLabels } from '../../dashboard-util/dashboard-utils.jsx';
import PropTypes from 'prop-types';

const NotStartedVsCompletedLineGraph = ({
  data,
  fetchData,
  customStart,
  customEnd,
  setCustomStart,
  setCustomEnd,
}) => {
  const storedSort = localStorage.getItem('lineGraph_sort') || 'Last 7 Days';
  const storedStart = localStorage.getItem('lineGraph_sort_start');
  const storedEnd = localStorage.getItem('lineGraph_sort_end');

  const [calendarShow, setCalendarShow] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [sort, setSort] = useState(storedSort);

  const prevSortRef = useRef(sort);
  const prevCustomStartRef = useRef(customStart);
  const prevCustomEndRef = useRef(customEnd);

  useEffect(() => {
    if (storedStart && storedEnd && storedStart !== 'undefined' && storedEnd !== 'undefined') {
      const start = new Date(storedStart);
      const end = new Date(storedEnd);
      setCustomStart(start);
      setCustomEnd(end);
      fetchData(start, end);
    } else {
      handleDateRange(storedSort);
    }
  }, []);

  const handleSelect = (selectedValue) => {
    if (['Last 7 Days', 'Last Month', 'Last 3 Months'].includes(selectedValue)) {
      setSort(selectedValue);
      handleDateRange(selectedValue);
    } else {
      if (sort !== 'Custom') {
        prevSortRef.current = sort;
        prevCustomStartRef.current = customStart;
        prevCustomEndRef.current = customEnd;
        setCustomStart(undefined);
        setCustomEnd(undefined);
      }
      setCalendarShow(true);
      setSort('Custom');
    }
  };

  const handleDateRange = (selectedValue) => {
    let startDate = new Date();
    let endDate = new Date();

    if (selectedValue === 'Last 7 Days') {
      startDate.setDate(startDate.getDate() - 6);
    } else if (selectedValue === 'Last Month') {
      startDate.setDate(startDate.getDate() - 30);
    } else if (selectedValue === 'Last 3 Months') {
      startDate.setDate(startDate.getDate() - 90);
    } else if (selectedValue === 'Custom') {
      startDate = customStart; 
      endDate = customEnd;
    }

    localStorage.setItem('lineGraph_sort', selectedValue);
    localStorage.setItem('lineGraph_sort_start', startDate.toISOString());
    localStorage.setItem('lineGraph_sort_end', endDate.toISOString());

    if (
      selectedValue === 'Custom' ||
      !customStart ||
      !customEnd ||
      startDate.getTime() !== customStart.getTime() ||
      endDate.getTime() !== customEnd.getTime()
    ) {
      setCustomStart(startDate);
      setCustomEnd(endDate);
  
      clearTimeout(window.lineGraphFetchTimeout);
      window.lineGraphFetchTimeout = setTimeout(() => {
        fetchData(startDate, endDate);
        if (sort === 'Custom') {
          setSort(`${dayjs(startDate).format('MM/DD/YYYY')} - ${dayjs(endDate).format('MM/DD/YYYY')}`);
        }
      }, 300); 
    } 
    setCalendarShow(false);
  };

  const handleCalendarClose = () => {
    if (!customStart || !customEnd) {
      setSort(prevSortRef.current);
      setCustomStart(prevCustomStartRef.current);
      setCustomEnd(prevCustomEndRef.current);
    } else {
      localStorage.setItem('lineGraph_sort', 'Custom');
      localStorage.setItem('lineGraph_sort_start', customStart.toISOString());
      localStorage.setItem('lineGraph_sort_end', customEnd.toISOString());
    }
    setCalendarShow(false);
  };

  useEffect(() => {
    if (customStart && customEnd) {
      fetchData(customStart, customEnd);
    }
  }, [customStart, customEnd]);

  const renderLineGraph = useMemo(() => {
    const formattedLabels = formatLabels(data.Labels || []);
    return (
      <LineGraph
        xlabels={formattedLabels}
        label1="Closed"
        label2="Not Started"
        dateForLabel1={data.ClosedData || []}
        dateForLabel2={data.NotStartedData || []}
        isMagnified={false}
      />
    );
  }, [data]);

  return (
    <div className="active-strs-doughnut-chart-container">
      <div className="active-str-doughnut-chart-header" style={{ height: "42.5px" }}>
        <div className="active-str-doughnut-header-left">
          <h4 className="active-str-doughnut-panel-title">
            Not Started vs. Closed STRs from &nbsp;
          </h4>
          <SortBySelect
            value={sort}
            handleSelect={handleSelect}
            options={['Last 7 Days', 'Last Month', 'Last 3 Months', 'Custom']}
            type={0}
            isMagnified={modalShow}
          />
          <Modal
            show={calendarShow}
            onHide={handleCalendarClose}
            className="calendar-modal"
            contentClassName="calendar-modal-content"
            backdropClassName="calendar-modal-backdrop"
            dialogClassName="completed-vs-overdue-modal-dialog"
          >
            <DateRangePicker
              start={customStart}
              setStart={setCustomStart}
              end={customEnd}
              setEnd={setCustomEnd}
            />
          </Modal>
        </div>
        <FluentButton
          appearance="none"
          onClick={() => setModalShow(true)}
          icon={
            <FluentUiIcon
              name="ArrowMaximizeRegular"
              color="#0F6CBD"
              size={20}
            />
          }
        />
      </div>
      <Modal
        show={modalShow}
        onHide={() => setModalShow(false)}
        dialogClassName="completed-vs-overdue-modal-dialog"
        contentClassName="completed-vs-overdue-modal-styles"
      >
        <Modal.Header className="p-0 w-100">
          <div className="completed-vs-overdue-modal-header-container d-flex">
            <h4 className="active-str-doughnut-panel-title" style={{ marginTop: '2px' }}>
              Not Started vs. Closed STRs from &nbsp;
            </h4>
            <SortBySelect
              value={sort}
              handleSelect={handleSelect}
              options={['Last 7 Days', 'Last Month', 'Last 3 Months', 'Custom']}
              type={0}
              isMagnified={modalShow}
            />
            <div className="d-flex flex-grow-1 justify-content-end align-items-center">
              <button
                className="completed-vs-overdue-exit-modal header-button"
                onClick={() => setModalShow(false)}
              ></button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-0">
          <LineGraph
            xlabels={formatLabels(data.Labels || [])}
            label1="Closed"
            label2="Not Started"
            dateForLabel1={data.ClosedData || []}
            dateForLabel2={data.NotStartedData || []}
            isMagnified={true}
          />
        </Modal.Body>
      </Modal>

      <div>{renderLineGraph}</div>
    </div>
  );
};

NotStartedVsCompletedLineGraph.propTypes = {
  data: PropTypes.shape({
    Labels: PropTypes.arrayOf(PropTypes.string),
    ClosedData: PropTypes.arrayOf(PropTypes.number),
    NotStartedData: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  fetchData: PropTypes.func.isRequired,
  customStart: PropTypes.instanceOf(Date),
  customEnd: PropTypes.instanceOf(Date),
  setCustomStart: PropTypes.func.isRequired,
  setCustomEnd: PropTypes.func.isRequired,
};

export default NotStartedVsCompletedLineGraph;
